import type { RecordingId } from "@repo/app-types/entities/recording";
import type { TeammateAccountCallOutline } from "@repo/app-types/entities/teammateAccountCallOutline";
import type {
  AccountGradingRecording,
  CustomerInteractionTextThread,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { popoverStyle } from "@repo/ui/popover/style";
import { useMemo, type FC } from "react";
import {
  Dialog,
  DialogTrigger,
  OverlayTriggerStateContext,
  Popover,
} from "react-aria-components";
import {
  buildTranscriptNavigationItems,
  checkCallSegmentContainsPosition,
  describeTranscriptNavigationItems,
  type CallPosition,
  type TranscriptPosition,
} from "./transcriptNavigationHelpers.js";
import {
  CallNavigationRow,
  TranscriptNavigator,
  TextConversationNavigationRow,
} from "./transcriptNavigator.js";
import { useCallAudioPosition } from "./useCallAudioPosition.js";
import { useTranscriptNavigationActions } from "./useTranscriptNavigationActions.js";

export interface TranscriptNavigatorDropdownProps {
  recordings: readonly AccountGradingRecording[];
  textThread?: CustomerInteractionTextThread;
  readingPosition: TranscriptPosition | undefined;
  callOutline: TeammateAccountCallOutline | undefined;
  isLoading?: boolean;
  isError?: boolean;
  /** Includes the current key moment and reserves its line when no moment matches. */
  showCurrentKeyMoment?: boolean;
  onPositionRequest: (position: TranscriptPosition) => void;
}

/** Compact navigation for calls, key moments, and text conversations. */
export const TranscriptNavigatorDropdown: FC<
  TranscriptNavigatorDropdownProps
> = (props) => {
  const {
    recordings,
    callOutline,
    textThread,
    showCurrentKeyMoment = false,
  } = props;
  const items = useMemo(
    () => buildTranscriptNavigationItems(recordings, callOutline, textThread),
    [recordings, callOutline, textThread],
  );
  const { selectedRecordingId, audioPosition } =
    useCallAudioPosition(recordings);
  // The trigger shows the call selected in the audio player, and the key moment under its playhead
  const activeItem =
    items.find(
      (item) => item.type === "call" && item.id === selectedRecordingId,
    ) ?? items[0];

  if (!activeItem) {
    return (
      <Button variant={ButtonVariant.Secondary} isDisabled>
        No calls available
      </Button>
    );
  }

  const currentKeyMoment =
    showCurrentKeyMoment && activeItem.type === "call"
      ? activeItem.segments.find((segment) =>
          checkCallSegmentContainsPosition(
            activeItem.id,
            segment,
            audioPosition,
          ),
        )
      : undefined;

  return (
    <DialogTrigger>
      <OverlayTriggerStateContext.Consumer>
        {(state) =>
          activeItem.type === "textConversation" ? (
            <TextConversationNavigationRow
              conversation={activeItem}
              isExpanded={state?.isOpen ?? false}
              reserveKeyMomentSpace={showCurrentKeyMoment}
            />
          ) : (
            <CallNavigationRow
              callNumber={activeItem.callNumber}
              recordedAt={activeItem.startedAt}
              duration={activeItem.duration}
              title={activeItem.title}
              keyMoment={currentKeyMoment}
              reserveKeyMomentSpace={showCurrentKeyMoment}
              isExpanded={state?.isOpen ?? false}
              className="static"
            />
          )
        }
      </OverlayTriggerStateContext.Consumer>
      <Popover
        placement="bottom start"
        className={popoverStyle().popover({
          className: "z-20 w-(--trigger-width)",
        })}
      >
        <Dialog
          aria-label={`Navigate ${describeTranscriptNavigationItems(items).toLowerCase()}`}
          className="outline-hidden"
        >
          {({ close }) => (
            <TranscriptNavigatorDropdownContent
              {...props}
              selectedRecordingId={selectedRecordingId}
              audioPosition={audioPosition}
              onClose={close}
            />
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};

interface TranscriptNavigatorDropdownContentProps extends TranscriptNavigatorDropdownProps {
  /** The call selected in the audio player; navigation opens at it. */
  selectedRecordingId: RecordingId | undefined;
  audioPosition: CallPosition | undefined;
  onClose: () => void;
}

const TranscriptNavigatorDropdownContent: FC<
  TranscriptNavigatorDropdownContentProps
> = (props) => {
  const {
    recordings,
    textThread,
    readingPosition,
    callOutline,
    isLoading,
    isError,
    onPositionRequest,
    selectedRecordingId,
    audioPosition,
    onClose,
  } = props;
  const { selectCall, selectSegment, selectTextConversation } =
    useTranscriptNavigationActions({
      recordings,
      onPositionRequest: (position) => {
        onPositionRequest(position);
        onClose();
      },
    });
  return (
    <div data-call-scroll-container className="max-h-[50vh] overflow-y-auto">
      <div className="px-2 pb-2">
        <TranscriptNavigator
          recordings={recordings}
          textThread={textThread}
          readingPosition={readingPosition}
          callOutline={callOutline}
          isOutlineLoading={isLoading}
          isOutlineError={isError}
          expandedRecordingId={selectedRecordingId}
          expandAll
          audioPosition={audioPosition}
          onCallRequest={selectCall}
          onSegmentRequest={selectSegment}
          onTextConversationRequest={selectTextConversation}
        />
      </div>
    </div>
  );
};
